import React, { useState, useEffect } from 'react';
import {
  CpuChipIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  ArrowPathIcon,
  ServerIcon,
  FireIcon,
  CloudIcon,
  WifiIcon,
  ChartBarIcon,
  DocumentTextIcon,
  Cog6ToothIcon,
  EyeIcon,
  PlayIcon,
  PauseIcon,
  StopIcon
} from '@heroicons/react/24/outline';
import LoadingScreen from '../components/common/LoadingScreen';
import CommonPageHeader from '../components/common/CommonPageHeader';
import { energiaService, SystemStatus, ProcessingLog } from '../services/energiaService';
import { useLanguage } from '../contexts/LanguageContext';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';


interface SystemHealth {
  overallHealth: number;
  databaseConnection: boolean;
  sftp_connection: boolean;
  hupx_api: boolean;
  firebase_status: boolean;
  last_successful_run: Date | null;
  next_scheduled_run: Date | null;
  active_processing: boolean;
  error_count_24h: number;
  uptime_percentage: number;
}

interface ProcessingStats {
  total_runs_today: number;
  successful_runs: number;
  failed_runs: number;
  avg_processing_time: number;
  last_24h_records: number;
  last_24h_customers: number;
  data_throughput: number;
  peak_memory_usage: number;
}

interface AlertItem {
  id: string;
  level: 'info' | 'warning' | 'error' | 'critical';
  message: string;
  timestamp: Date;
  component: string;
  resolved: boolean;
  details?: string;
}

const EnergiaMonitoring = () => {
  const { t, language, setLanguage } = useLanguage();
  const [loading, setLoading] = useState(true);

  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [processingStats, setProcessingStats] = useState<ProcessingStats | null>(null);
  const [systemStatuses, setSystemStatuses] = useState<SystemStatus[]>([]);
  const [recentLogs, setRecentLogs] = useState<ProcessingLog[]>([]);
  const [alerts, setAlerts] = useState<AlertItem[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<string>('all');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(30); // seconds

  useEffect(() => {
    loadMonitoringData();
    
    // Auto-refresh interval
    if (autoRefresh) {
      const interval = setInterval(loadMonitoringData, refreshInterval * 1000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval]);

  // Initialize demo alerts on component mount
  // Load data when component mounts or language changes
  useEffect(() => {
    loadMonitoringData();
  }, [language, t]);

  const loadMonitoringData = async () => {
    try {
      setLoading(true);
      
      // Load system data
      const [statuses, logs] = await Promise.all([
        energiaService.getSystemStatus(),
        energiaService.getProcessingLogs(undefined, 20)
      ]);

      setSystemStatuses(statuses);
      setRecentLogs(logs);

      // Generate mock system health data
      const health = generateSystemHealth(statuses, logs);
      setSystemHealth(health);

      // Generate mock processing stats
      const stats = generateProcessingStats(logs);
      setProcessingStats(stats);

          // Generate mock alerts
    const alertsData = generateAlerts(statuses, logs);
    setAlerts(alertsData);

    } catch (error) {
      console.error('Error loading monitoring data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateSystemHealth = (statuses: SystemStatus[], logs: ProcessingLog[]): SystemHealth => {
    const healthyProviders = statuses.filter(s => s.status === 'completed').length;
    const totalProviders = statuses.length;
    const overallHealth = totalProviders > 0 ? (healthyProviders / totalProviders) * 100 : 0;

    const recentErrors = logs.filter(log => 
      log.status === 'error' && 
      log.timestamp?.toDate &&
      (new Date().getTime() - log.timestamp.toDate().getTime()) < 24 * 60 * 60 * 1000
    ).length;

    return {
      overallHealth,
      databaseConnection: true,
      sftp_connection: healthyProviders > 0,
      hupx_api: Math.random() > 0.1,
      firebase_status: true,
      last_successful_run: logs.find(log => log.status === 'completed')?.timestamp?.toDate() || null,
      next_scheduled_run: new Date(Date.now() + 4 * 60 * 60 * 1000), // 4 hours from now
      active_processing: statuses.some(s => s.status === 'processing'),
      error_count_24h: recentErrors,
      uptime_percentage: Math.random() * 5 + 95
    };
  };

  const generateProcessingStats = (logs: ProcessingLog[]): ProcessingStats => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const todayLogs = logs.filter(log => 
      log.timestamp?.toDate && log.timestamp.toDate() >= today
    );

    return {
      total_runs_today: todayLogs.length,
      successful_runs: todayLogs.filter(log => log.status === 'completed').length,
      failed_runs: todayLogs.filter(log => log.status === 'error').length,
      avg_processing_time: Math.random() * 300 + 60, // 60-360 seconds
      last_24h_records: Math.floor(Math.random() * 50000) + 10000,
      last_24h_customers: Math.floor(Math.random() * 100) + 50,
      data_throughput: Math.random() * 1000 + 500, // MB/hour
      peak_memory_usage: Math.random() * 512 + 256 // MB
    };
  };

  const generateAlerts = (statuses: SystemStatus[], logs: ProcessingLog[]): AlertItem[] => {
    const alerts: AlertItem[] = [];
    const now = new Date();

    // Enhanced critical alerts for failed providers
    statuses.filter(s => s.status === 'error' || s.status === 'critical_error').forEach(status => {
      const lastRun = status.timestamp?.toDate();
      const timeSinceError = lastRun ? Math.floor((now.getTime() - lastRun.getTime()) / (1000 * 60)) : 0;
      const errorDetails = status.details || {};
      
      let errorMessage = `${status.provider.toUpperCase()} szolgáltató hiba`;
      let detailedMessage = '';
      
      // Add specific error context based on provider and details
      if (status.provider === 'elmu') {
        if (errorDetails.error_message?.includes('authentication') || errorDetails.error_message?.includes('login')) {
          detailedMessage = 'Hitelesítési hiba - ellenőrizze a bejelentkezési adatokat';
        } else if (errorDetails.error_message?.includes('connection')) {
          detailedMessage = 'Kapcsolódási hiba - hálózati probléma';
        } else {
          detailedMessage = 'Ismeretlen ELMU hiba';
        }
      } else if (status.provider === 'emasz') {
        if (errorDetails.error_message?.includes('authentication')) {
          detailedMessage = 'Hitelesítési hiba - ellenőrizze a bejelentkezési adatokat';
        } else {
          detailedMessage = 'Ismeretlen EMASZ hiba';
        }
      } else {
        detailedMessage = errorDetails.error_message || `Ismeretlen ${status.provider.toUpperCase()} hiba`;
      }
      
      // Add time context
      if (timeSinceError > 0) {
        detailedMessage = `${detailedMessage} (${timeSinceError} perce)`;
      }

      alerts.push({
        id: `alert_${status.provider}_${Date.now()}`,
        level: status.status === 'critical_error' ? 'critical' : 'error',
        message: errorMessage,
        timestamp: lastRun || new Date(),
        component: status.provider,
        resolved: false,
        details: detailedMessage
      });
    });

    // Enhanced memory usage warning with actual data
    const recentLogs = logs.filter(log => {
      const logTime = log.timestamp?.toDate();
      return logTime && (now.getTime() - logTime.getTime()) < 60 * 60 * 1000; // Last hour
    });

    const highMemoryLogs = recentLogs.filter(log => 
      log.processing_details?.performance_metrics?.memory_usage_mb > 400
    );

    if (highMemoryLogs.length > 0) {
      const maxMemory = Math.max(...highMemoryLogs.map(log => 
        log.processing_details?.performance_metrics?.memory_usage_mb || 0
      ));
      
      alerts.push({
        id: `memory_warning_${Date.now()}`,
        level: 'warning',
        message: 'Magas memóriahasználat',
        timestamp: new Date(),
        component: 'system',
        resolved: false,
        details: `Csúcs memóriahasználat: ${Math.round(maxMemory)} MB (${highMemoryLogs.length} folyamat)`
      });
    }

    // New: Stale data warning
    const staleProviders = statuses.filter(status => {
      const lastUpdate = status.timestamp?.toDate();
      if (!lastUpdate) return true;
      const hoursSinceUpdate = (now.getTime() - lastUpdate.getTime()) / (1000 * 60 * 60);
      return hoursSinceUpdate > 24 && status.status === 'completed';
    });

    if (staleProviders.length > 0) {
      staleProviders.forEach(provider => {
        const lastUpdate = provider.timestamp?.toDate();
        const daysSinceUpdate = lastUpdate ? 
          Math.floor((now.getTime() - lastUpdate.getTime()) / (1000 * 60 * 60 * 24)) : 0;
        
        alerts.push({
          id: `stale_${provider.provider}_${Date.now()}`,
          level: 'warning',
          message: `${provider.provider.toUpperCase()} elavult adatok`,
          timestamp: lastUpdate || new Date(),
          component: provider.provider,
          resolved: false,
          details: `Utolsó frissítés ${daysSinceUpdate} napja - adatok elavultak`
        });
      });
    }

    // New: Processing performance warning
    const slowProcesses = recentLogs.filter(log => 
      log.processing_details?.performance_metrics?.processing_duration_seconds > 1800 // 30 minutes
    );

    if (slowProcesses.length > 0) {
      slowProcesses.forEach(log => {
        const duration = Math.round(log.processing_details?.performance_metrics?.processing_duration_seconds / 60);
        alerts.push({
          id: `slow_process_${log.provider}_${Date.now()}`,
          level: 'warning',
          message: `${log.provider.toUpperCase()} lassú feldolgozás`,
          timestamp: log.timestamp?.toDate() || new Date(),
          component: log.provider,
          resolved: false,
          details: `Feldolgozási idő: ${duration} perc (${log.processing_details?.records_processed?.toLocaleString('hu-HU') || 'N/A'} rekord)`
        });
      });
    }

    // New: Data quality alerts
    const poorQualityLogs = recentLogs.filter(log => 
      log.processing_details?.data_quality_metrics?.completeness_percentage < 80
    );

    if (poorQualityLogs.length > 0) {
      poorQualityLogs.forEach(log => {
        const completeness = log.processing_details?.data_quality_metrics?.completeness_percentage;
        alerts.push({
          id: `quality_${log.provider}_${Date.now()}`,
          level: 'warning',
          message: `${log.provider.toUpperCase()} gyenge adatminőség`,
          timestamp: log.timestamp?.toDate() || new Date(),
          component: log.provider,
          resolved: false,
          details: `Adatteljességi arány: ${completeness?.toFixed(1) || 'N/A'}% (${log.processing_details?.data_quality_metrics?.invalid_measurements || 0} érvénytelen mérés)`
        });
      });
    }

    return alerts.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  };

  const getHealthColor = (percentage: number) => {
    if (percentage >= 90) return 'text-green-600 bg-green-100';
    if (percentage >= 70) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getAlertIcon = (level: string) => {
    switch (level) {
      case 'critical':
        return <XCircleIcon className="w-5 h-5 text-red-500" />;
      case 'error':
        return <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />;
      case 'warning':
        return <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />;
      default:
        return <CheckCircleIcon className="w-5 h-5 text-blue-500" />;
    }
  };

  const toggleAutoRefresh = () => {
    setAutoRefresh(!autoRefresh);
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="space-y-6 pb-20 sm:pb-0">
      {/* Header */}
      <CommonPageHeader
        title="Energia Monitoring"
        subtitle="Rendszer állapot és teljesítmény figyelés"
        icon={CpuChipIcon}
        onRefresh={loadMonitoringData}
        refreshButtonText="Frissítés most"
      >
        <div className="flex items-center space-x-2 bg-white/20 backdrop-blur-sm rounded-lg px-3 py-2">
          <span className="text-sm text-indigo-100">Automatikus frissítés:</span>
          <button
            onClick={toggleAutoRefresh}
            className={`p-1 rounded ${autoRefresh ? 'text-green-300' : 'text-indigo-200'}`}
          >
            {autoRefresh ? <PlayIcon className="w-4 h-4" /> : <PauseIcon className="w-4 h-4" />}
          </button>
          <div className="flex items-center space-x-1">
            {[10, 30, 60, 300].map((interval) => (
              <button
                key={interval}
                onClick={() => setRefreshInterval(interval)}
                disabled={!autoRefresh}
                className={`px-2 py-1 text-xs rounded transition-colors ${
                  refreshInterval === interval
                    ? 'bg-white/30 text-white font-semibold'
                    : 'bg-white/10 text-indigo-200 hover:bg-white/20 hover:text-white'
                } ${!autoRefresh ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {interval === 60 ? '1m' : interval === 300 ? '5m' : `${interval}s`}
              </button>
            ))}
          </div>
        </div>
      </CommonPageHeader>



      {/* System Health Overview */}
      {systemHealth && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-xl p-6 shadow-md border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Rendszer egészség</p>
                <p className={`text-2xl font-semibold mt-1 ${getHealthColor(systemHealth.overallHealth).split(' ')[0]}`}>
                  {systemHealth.overallHealth.toFixed(1)}%
                </p>
              </div>
              <div className={`p-3 rounded-lg ${getHealthColor(systemHealth.overallHealth).split(' ')[1]}`}>
                <CpuChipIcon className={`w-6 h-6 ${getHealthColor(systemHealth.overallHealth).split(' ')[0]}`} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-md border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Uptime</p>
                <p className="text-green-600 text-2xl font-semibold mt-1">
                  {systemHealth.uptime_percentage.toFixed(1)}%
                </p>
              </div>
              <CheckCircleIcon className="w-8 h-8 text-green-500" />
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-md border-l-4 border-yellow-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">24h hibák</p>
                <p className="text-yellow-600 text-2xl font-semibold mt-1">
                  {systemHealth.error_count_24h}
                </p>
              </div>
              <ExclamationTriangleIcon className="w-8 h-8 text-yellow-500" />
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-md border-l-4 border-purple-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Aktív feldolgozás</p>
                <p className="text-purple-600 text-2xl font-semibold mt-1">
                  {systemHealth.active_processing ? 'IGEN' : 'NEM'}
                </p>
              </div>
              {systemHealth.active_processing ? (
                <ArrowPathIcon className="w-8 h-8 text-purple-500 animate-spin" />
              ) : (
                <ClockIcon className="w-8 h-8 text-purple-500" />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Service Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-md">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Szolgáltatás állapotok</h3>
          <div className="space-y-3">
            {systemHealth && (
              <>
                <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                  <div className="flex items-center space-x-3">
                    <ServerIcon className="w-5 h-5 text-gray-500" />
                    <span className="text-sm font-medium">Database</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {systemHealth.databaseConnection ? (
                      <CheckCircleIcon className="w-5 h-5 text-green-500" />
                    ) : (
                      <XCircleIcon className="w-5 h-5 text-red-500" />
                    )}
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      systemHealth.databaseConnection ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {systemHealth.databaseConnection ? 'Aktív' : 'Offline'}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                  <div className="flex items-center space-x-3">
                    <CloudIcon className="w-5 h-5 text-gray-500" />
                    <span className="text-sm font-medium">Firebase</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {systemHealth.firebase_status ? (
                      <CheckCircleIcon className="w-5 h-5 text-green-500" />
                    ) : (
                      <XCircleIcon className="w-5 h-5 text-red-500" />
                    )}
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      systemHealth.firebase_status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {systemHealth.firebase_status ? 'Kapcsolódva' : 'Hiba'}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                  <div className="flex items-center space-x-3">
                    <WifiIcon className="w-5 h-5 text-gray-500" />
                    <span className="text-sm font-medium">SFTP</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {systemHealth.sftp_connection ? (
                      <CheckCircleIcon className="w-5 h-5 text-green-500" />
                    ) : (
                      <XCircleIcon className="w-5 h-5 text-red-500" />
                    )}
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      systemHealth.sftp_connection ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {systemHealth.sftp_connection ? 'Kapcsolódva' : 'Hiba'}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                  <div className="flex items-center space-x-3">
                    <ChartBarIcon className="w-5 h-5 text-gray-500" />
                    <span className="text-sm font-medium">HUPX API</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {systemHealth.hupx_api ? (
                      <CheckCircleIcon className="w-5 h-5 text-green-500" />
                    ) : (
                      <XCircleIcon className="w-5 h-5 text-red-500" />
                    )}
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      systemHealth.hupx_api ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {systemHealth.hupx_api ? 'Elérhető' : 'Hiba'}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Processing Statistics */}
        <div className="bg-white rounded-xl p-6 shadow-md">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Feldolgozási statisztikák</h3>
          {processingStats && (
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Mai futások:</span>
                <span className="text-sm font-medium">{processingStats.total_runs_today}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Sikeres:</span>
                <span className="text-sm font-medium text-green-600">{processingStats.successful_runs}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Sikertelen:</span>
                <span className="text-sm font-medium text-red-600">{processingStats.failed_runs}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Átlag idő:</span>
                <span className="text-sm font-medium">{Math.round(processingStats.avg_processing_time)}s</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">24h rekordok:</span>
                <span className="text-sm font-medium">{processingStats.last_24h_records.toLocaleString('hu-HU')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">24h ügyfelek:</span>
                <span className="text-sm font-medium">{processingStats.last_24h_customers}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Adatátvitel:</span>
                <span className="text-sm font-medium">{Math.round(processingStats.data_throughput)} MB/h</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Csúcs memória:</span>
                <span className="text-sm font-medium">{Math.round(processingStats.peak_memory_usage)} MB</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Alerts */}
      <div className="bg-white rounded-xl p-6 shadow-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Aktív riasztások</h3>
          {alerts.length > 0 && (
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2 text-xs">
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  <span className="text-gray-600">{alerts.filter(a => a.level === 'critical' || a.level === 'error').length} kritikus</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                  <span className="text-gray-600">{alerts.filter(a => a.level === 'warning').length} figyelmeztetés</span>
                </div>
              </div>
              <button
                onClick={() => setAlerts([])}
                className="text-xs px-3 py-1 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Összes bezárása
              </button>
            </div>
          )}
        </div>
        
        {alerts.length === 0 ? (
          <div className="text-center py-8">
            <CheckCircleIcon className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <p className="text-gray-600 text-sm">Nincsenek aktív riasztások</p>
            <p className="text-gray-500 text-xs mt-1">A rendszer megfelelően működik</p>
          </div>
        ) : (
          <div className="space-y-3">
            {alerts.map(alert => (
              <div key={alert.id} className={`flex items-start justify-between p-4 rounded-xl border-l-4 ${
                alert.level === 'critical' ? 'bg-red-50 border-red-500' :
                alert.level === 'error' ? 'bg-red-50 border-red-400' :
                alert.level === 'warning' ? 'bg-yellow-50 border-yellow-500' : 'bg-blue-50 border-blue-500'
              }`}>
                <div className="flex items-start space-x-3 flex-1">
                  {getAlertIcon(alert.level)}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-gray-900">{alert.message}</div>
                    {alert.details && (
                      <div className="text-sm text-gray-700 mt-1 leading-relaxed">
                        {alert.details}
                      </div>
                    )}
                    <div className="flex items-center space-x-3 text-xs text-gray-500 mt-2">
                      <span className="flex items-center space-x-1">
                        <span className="font-medium">{alert.component.toUpperCase()}</span>
                      </span>
                      <span>•</span>
                      <span>{format(alert.timestamp, 'HH:mm:ss', { locale: hu })}</span>
                      <span>•</span>
                      <span>{format(alert.timestamp, 'MM.dd', { locale: hu })}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2 ml-3">
                  <span className={`text-xs px-3 py-1 rounded-full font-semibold ${
                    alert.level === 'critical' ? 'bg-red-100 text-red-800' :
                    alert.level === 'error' ? 'bg-red-100 text-red-700' :
                    alert.level === 'warning' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'
                  }`}>
                    {alert.level === 'critical' ? 'KRITIKUS' :
                     alert.level === 'error' ? 'HIBA' :
                     alert.level === 'warning' ? 'FIGYELMEZTETÉS' : 'INFO'}
                  </span>
                  <button
                    onClick={() => setAlerts(alerts.filter(a => a.id !== alert.id))}
                    className="p-1 rounded-full hover:bg-gray-200 transition-colors"
                    title="Riasztás bezárása"
                  >
                    <XCircleIcon className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Provider Status Table */}
      <div className="bg-white rounded-xl p-6 shadow-md">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Szolgáltatói állapotok</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Szolgáltató</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Állapot</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Egészség</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Utolsó frissítés</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hibák</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {systemStatuses.map((status) => (
                <tr key={status.provider} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    {status.provider.toUpperCase()}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <div className="flex items-center space-x-2">
                      {status.status === 'completed' ? (
                        <CheckCircleIcon className="w-4 h-4 text-green-500" />
                      ) : status.status === 'processing' ? (
                        <ArrowPathIcon className="w-4 h-4 text-blue-500 animate-spin" />
                      ) : (
                        <XCircleIcon className="w-4 h-4 text-red-500" />
                      )}
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        status.status === 'completed' ? 'bg-green-100 text-green-800' :
                        status.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {status.status === 'completed' ? 'Aktív' :
                         status.status === 'processing' ? 'Feldolgozás' :
                         'Hiba'
                        }
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm">
                    {status.monitoring?.health_score && (
                      <div className="flex items-center space-x-2">
                        <span className="text-gray-900">{status.monitoring.health_score}%</span>
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              status.monitoring.health_score >= 90 ? 'bg-green-500' :
                              status.monitoring.health_score >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${status.monitoring.health_score}%` }}
                          ></div>
                        </div>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {status.timestamp?.toDate ? 
                      format(status.timestamp.toDate(), 'MM.dd HH:mm', { locale: hu }) :
                      'N/A'
                    }
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {status.monitoring?.consecutive_failures || 0}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl p-6 shadow-md">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Legutóbbi aktivitás</h3>
        <div className="space-y-3">
          {recentLogs.slice(0, 10).map((log, index) => (
            <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
              <div className="flex items-center space-x-3">
                {log.status === 'completed' ? (
                  <CheckCircleIcon className="w-4 h-4 text-green-500" />
                ) : log.status === 'processing' ? (
                  <ArrowPathIcon className="w-4 h-4 text-blue-500 animate-spin" />
                ) : (
                  <XCircleIcon className="w-4 h-4 text-red-500" />
                )}
                <div>
                  <div className="text-sm font-medium text-gray-800">
                    {log.provider.toUpperCase()} feldolgozás
                  </div>
                  <div className="text-xs text-gray-500">
                    {log.processing_details?.records_processed?.toLocaleString('hu-HU') || 'N/A'} rekord
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-gray-500">
                  {log.timestamp?.toDate ? 
                    format(log.timestamp.toDate(), 'HH:mm:ss', { locale: hu }) :
                    'N/A'
                  }
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  log.status === 'completed' ? 'bg-green-100 text-green-800' :
                  log.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {log.status === 'completed' ? 'Sikeres' :
                   log.status === 'processing' ? 'Folyamatban' :
                   'Hiba'
                  }
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default EnergiaMonitoring; 